import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/images/Cognidoor Logo.png';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../services/firebase';
import { signOut } from 'firebase/auth';
import useAuthUser from '../services/useAuthUser';
import { useTrack } from '../contexts/TrackContext';

const SCROLL_THRESHOLD = 200;

const MCK_PATHS = ['/seawolf', '/seawolf-practice', '/redrock', '/ecosystem-solver', '/ecosystemguide', '/mckinsey-ai-coach', '/mckinsey-lilli-ai-coach', '/about-solve', '/demo-ecosystem-solver'];
const FREE_RESOURCES_PATHS = ['/about-solve', '/about-bcg-casey', '/consulting-math-guides'];
const isBcgPage = (pathname) => pathname === '/about-bcg-casey' || pathname.startsWith('/bcg-');
const isMcKinseyPage = (pathname) => MCK_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
const isFreeResourcesPage = (pathname) => FREE_RESOURCES_PATHS.includes(pathname);

function TrackSwitcher() {
  const { pathname } = useLocation();
  const { track, setTrack } = useTrack();
  const wrapperRef = useRef(null);
  const onHome = pathname === '/';
  const items = onHome
    ? [
        { value: 'all', label: 'All' },
        { value: 'mck', label: 'McKinsey' },
        { value: 'bcg', label: 'BCG' },
      ]
    : [
        { value: 'mck', label: 'McKinsey' },
        { value: 'bcg', label: 'BCG' },
      ];
  const selectedTrack = track;

  useEffect(() => {
    const t = setTimeout(() => {
      wrapperRef.current?.classList.add('track-selector-stopped');
    }, 10000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div ref={wrapperRef} className="relative inline-flex">
      <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible" viewBox="0 0 220 50" preserveAspectRatio="none" aria-hidden>
        <defs>
          <linearGradient id="track-selector-flash-gradient-nav" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0B5A96" />
            <stop offset="45%" stopColor="#38bdf8" />
            <stop offset="55%" stopColor="#7dd3fc" />
            <stop offset="100%" stopColor="#0B5A96" />
          </linearGradient>
          <filter id="track-selector-flash-glow-nav" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
            <feColorMatrix in="blur" type="matrix" values="0 0 0 0 0.2  0 0 0 0 0.5  0 0 0 0 1  0 0 0 0.9 0" result="glow" />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path
          className="track-selector-border-path"
          d="M 14,1 L 207,1 Q 219,1 219,14 L 219,36 Q 219,49 206,49 L 14,49 Q 1,49 1,36 L 1,14 Q 1,1 14,1"
          fill="none"
          stroke="url(#track-selector-flash-gradient-nav)"
          strokeWidth="6"
          strokeLinecap="round"
          pathLength="1"
          strokeDasharray="0.22 0.78"
          filter="url(#track-selector-flash-glow-nav)"
        />
      </svg>
      <div className="track-selector-attention relative inline-flex border rounded-2xl p-1 border-slate-200 bg-slate-50">
        {items.map((it) => (
          <button
            key={it.value}
            onClick={() => setTrack(it.value)}
            className={`relative px-4 py-2 text-sm transition-all duration-300 rounded-xl ${
              selectedTrack === it.value
                ? 'text-white bg-gradient-to-r from-blue-600 to-blue-400 shadow-md'
                : 'text-slate-600 hover:bg-white/60'
            }`}
          >
            {it.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Header() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { track, setTrack } = useTrack();
  const [scrollY, setScrollY] = useState(typeof window !== 'undefined' ? window.scrollY : 0);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const { user } = useAuthUser();

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Sync track to current page when on a BCG or McKinsey page so nav shows the right menu
  useEffect(() => {
    if (isBcgPage(pathname)) setTrack('bcg');
    else if (isMcKinseyPage(pathname)) setTrack('mck');
  }, [pathname, setTrack]);

  const isLanding = pathname === '/';
  const showTrackInNav =
    !isFreeResourcesPage(pathname) &&
    ((isLanding && scrollY >= SCROLL_THRESHOLD) || isBcgPage(pathname) || isMcKinseyPage(pathname));

  const ecosystemDropdownRef = useRef(null);
  const redrockDropdownRef = useRef(null);
  const seawolfDropdownRef = useRef(null);
  const resourcesDropdownRef = useRef(null);
  const mckinseyDropdownRef = useRef(null);
  const bcgDropdownRef = useRef(null);
  const bcgTrackDropdownRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  // McKinsey sub-menu (second-level flyout) when in All track: 'seawolf' | 'redrock' | 'ecosystem' | null
  const [mckinseySubMenu, setMckinseySubMenu] = useState(null);
  // Mobile: which McKinsey sub is expanded when in All track
  const [mobileMckinseySub, setMobileMckinseySub] = useState(null);
  // BCG dropdown (All track) second-level: 'kc' | null
  const [bcgSubMenu, setBcgSubMenu] = useState(null);
  // Mobile: which BCG sub is expanded when in All track
  const [mobileBcgSub, setMobileBcgSub] = useState(null);

  // Close dropdown when clicking outside (desktop only)
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        (activeDropdown === 'ecosystem' && ecosystemDropdownRef.current && !ecosystemDropdownRef.current.contains(event.target)) ||
        (activeDropdown === 'redrock' && redrockDropdownRef.current && !redrockDropdownRef.current.contains(event.target)) ||
        (activeDropdown === 'seawolf' && seawolfDropdownRef.current && !seawolfDropdownRef.current.contains(event.target)) ||
        (activeDropdown === 'resources' && resourcesDropdownRef.current && !resourcesDropdownRef.current.contains(event.target)) ||
        (activeDropdown === 'mckinsey' && mckinseyDropdownRef.current && !mckinseyDropdownRef.current.contains(event.target)) ||
        (activeDropdown === 'bcg' && bcgDropdownRef.current && !bcgDropdownRef.current.contains(event.target)) ||
        (activeDropdown === 'bcg_track' && bcgTrackDropdownRef.current && !bcgTrackDropdownRef.current.contains(event.target))
      ) {
        setActiveDropdown(null);
        setMckinseySubMenu(null);
        setBcgSubMenu(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [activeDropdown]);

  // When closing dropdowns, clear sub-menus
  useEffect(() => {
    if (activeDropdown !== 'mckinsey') setMckinseySubMenu(null);
    if (activeDropdown !== 'bcg') setBcgSubMenu(null);
  }, [activeDropdown]);

  // Pricing nav: link to /#pricing so landing scrolls to pricing section (from any page or when already on home)
  const handlePricingClick = () => setMobileMenuOpen(false);

  const toggleDropdown = (dropdownName) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const handleMouseEnter = (dropdownName) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setActiveDropdown(dropdownName);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 100); // Small delay to prevent accidental closing
  };

  const handleDropdownLinkClick = () => {
    setActiveDropdown(null);
    setMckinseySubMenu(null);
    setBcgSubMenu(null);
    setMobileMenuOpen(false);
    setMobileDropdown(null);
  };

  // MOBILE: Toggle mobile menu and dropdowns
  const handleMobileDropdown = (dropdownName) => {
    setMobileDropdown(mobileDropdown === dropdownName ? null : dropdownName);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
      setMobileMenuOpen(false);
    } catch (error) {
      // console.error('Error signing out:', error);
    }
  };

  return (
    <nav className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Main nav container */}
      <div className="flex justify-between items-center px-4 sm:px-8 md:px-16 py-1">
        {/* Logo */}
        <Link
          to="/"
          onClick={() => { setMobileMenuOpen(false); setMobileDropdown(null); }}
          className="inline-block border-0 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
        >
          <img src={logo} alt="Cognidoor Logo" className="h-10 sm:h-[50px] w-auto block border-0" />
        </Link>

        {/* Hamburger (mobile only) */}
        <button
          className="sm:hidden flex items-center justify-center p-2 rounded focus:outline-none"
          aria-label="Open menu"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <svg className="h-7 w-7 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
            )}
          </svg>
        </button>

        {/* Desktop nav */}
        <div className="hidden sm:flex items-center space-x-6">
          {showTrackInNav && <TrackSwitcher />}
          <nav aria-label="main navigation">
            <ul className="flex items-center space-x-6 overflow-visible">
              <AnimatePresence mode="wait">
                {/* Track: All — McKinsey dropdown (Seawolf, Red Rock, Ecosystem) with second-level flyout; BCG */}
                {track === 'all' && (
                  <motion.li
                    key="all"
                    className="flex items-center space-x-6"
                    style={{ listStyle: 'none' }}
                    initial={{ opacity: 0, x: 14 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -14 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                  >
                    <div className="flex items-center space-x-6">
                  <li
                    className="relative"
                    ref={mckinseyDropdownRef}
                    onMouseEnter={() => handleMouseEnter('mckinsey')}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      type="button"
                      onClick={() => toggleDropdown('mckinsey')}
                      className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-inter text-[15px] flex items-center gap-1"
                    >
                      McKinsey
                      <svg className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'mckinsey' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {/* First-level panel + second-level flyout side by side */}
                    <div
                      className={`absolute left-0 top-full pt-2 z-50 flex transition-all duration-200 ease-out ${activeDropdown === 'mckinsey' ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none -translate-y-1'}`}
                    >
                      {activeDropdown === 'mckinsey' && (
                        <>
                          <ul className="bg-white shadow-lg rounded-lg py-2 min-w-[200px] border border-gray-100">
                            <li onMouseEnter={() => setMckinseySubMenu(null)}>
                              <Link to="/mckinsey-ai-coach" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 text-[14px] font-inter text-gray-600 hover:bg-blue-50 hover:text-blue-600 border-l-2 border-transparent -ml-px">
                                McKinsey Lilli AI Assessment Coach
                              </Link>
                            </li>
                            <li
                              onMouseEnter={() => setMckinseySubMenu('seawolf')}
                              className={`px-4 py-2.5 cursor-pointer text-[14px] font-inter border-l-2 -ml-px ${mckinseySubMenu === 'seawolf' ? 'border-blue-500 bg-blue-50/80 text-blue-700' : 'border-transparent text-gray-600 hover:bg-blue-50 hover:text-blue-600'}`}
                              onClick={() => setMckinseySubMenu(mckinseySubMenu === 'seawolf' ? null : 'seawolf')}
                            >
                              McKinsey SeaWolf Game
                            </li>
                            <li
                              onMouseEnter={() => setMckinseySubMenu('redrock')}
                              className={`px-4 py-2.5 cursor-pointer text-[14px] font-inter border-l-2 -ml-px ${mckinseySubMenu === 'redrock' ? 'border-blue-500 bg-blue-50/80 text-blue-700' : 'border-transparent text-gray-600 hover:bg-blue-50 hover:text-blue-600'}`}
                              onClick={() => setMckinseySubMenu(mckinseySubMenu === 'redrock' ? null : 'redrock')}
                            >
                              McKinsey RedRock
                            </li>
                            <li
                              onMouseEnter={() => setMckinseySubMenu('ecosystem')}
                              className={`px-4 py-2.5 cursor-pointer text-[14px] font-inter border-l-2 -ml-px ${mckinseySubMenu === 'ecosystem' ? 'border-blue-500 bg-blue-50/80 text-blue-700' : 'border-transparent text-gray-600 hover:bg-blue-50 hover:text-blue-600'}`}
                              onClick={() => setMckinseySubMenu(mckinseySubMenu === 'ecosystem' ? null : 'ecosystem')}
                            >
                              McKinsey Ecosystem Game
                            </li>
                          </ul>
                          {/* Second-level flyout — smooth slide + fade */}
                          <div
                            className={`overflow-hidden ml-1 transition-all duration-200 ease-out ${mckinseySubMenu ? 'w-72 opacity-100 translate-x-0' : 'w-0 opacity-0 -translate-x-2 ml-0'}`}
                            style={{ minWidth: mckinseySubMenu ? 288 : 0 }}
                          >
                            <ul className="bg-white shadow-lg rounded-lg py-2 border border-gray-100 w-72">
                              {mckinseySubMenu === 'seawolf' && (
                                <>
                                  <li>
                                    <Link to="/seawolf-practice" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                                      ⚡ McKinsey Sea Wolf Simulation
                                    </Link>
                                  </li>
                                  <li>
                                    <Link to="/seawolf" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                                      🌊 McKinsey Sea Wolf Guide & Solver
                                    </Link>
                                  </li>
                                </>
                              )}
                              {mckinseySubMenu === 'redrock' && (
                                <>
                                  <li>
                                    <Link to="/redrock" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                                      🔥 McKinsey Red Rock Simulator
                                    </Link>
                                  </li>
                                  <li>
                                    <Link to="/redrock-guide" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                                      🏔️ McKinsey Red Rock Guide
                                    </Link>
                                  </li>
                                </>
                              )}
                              {mckinseySubMenu === 'ecosystem' && (
                                <>
                                  <li>
                                    <Link to="/ecosystem-solver" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                                      🚀 McKinsey Ecosystem Solver
                                    </Link>
                                  </li>
                                  <li>
                                    <Link to="/ecosystemguide" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                                      🎯 McKinsey Ecosystem Guide
                                    </Link>
                                  </li>
                                </>
                              )}
                            </ul>
                          </div>
                        </>
                      )}
                    </div>
                  </li>
                  {/* BCG dropdown in All track: BCG Casey (Simulation, Guide) + Candidate-Led AI Coach link */}
                  <li
                    className="relative"
                    ref={bcgDropdownRef}
                    onMouseEnter={() => handleMouseEnter('bcg')}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      type="button"
                      onClick={() => toggleDropdown('bcg')}
                      className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-inter text-[15px] flex items-center gap-1"
                    >
                      BCG
                      <svg className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'bcg' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div
                      className={`absolute left-0 top-full pt-2 z-50 flex transition-all duration-200 ease-out ${activeDropdown === 'bcg' ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none -translate-y-1'}`}
                    >
                      {activeDropdown === 'bcg' && (
                        <>
                          <ul className="bg-white shadow-lg rounded-lg py-2 min-w-[200px] border border-gray-100">
                            <li>
                              <Link to="/bcg-ai-coach" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 text-[14px] font-inter text-gray-600 hover:bg-blue-50 hover:text-blue-600 border-l-2 border-transparent -ml-px">
                                BCG Candidate-Led AI Coach
                              </Link>
                            </li>
                            <li
                              onMouseEnter={() => setBcgSubMenu('casey')}
                              className={`px-4 py-2.5 cursor-pointer text-[14px] font-inter border-l-2 -ml-px ${bcgSubMenu === 'casey' ? 'border-blue-500 bg-blue-50/80 text-blue-700' : 'border-transparent text-gray-600 hover:bg-blue-50 hover:text-blue-600'}`}
                              onClick={() => setBcgSubMenu(bcgSubMenu === 'casey' ? null : 'casey')}
                            >
                              BCG Casey
                            </li>
                          </ul>
                          <div
                            className={`overflow-hidden ml-1 transition-all duration-200 ease-out ${bcgSubMenu ? 'w-72 opacity-100 translate-x-0' : 'w-0 opacity-0 -translate-x-2 ml-0'}`}
                            style={{ minWidth: bcgSubMenu ? 288 : 0 }}
                          >
                            <ul className="bg-white shadow-lg rounded-lg py-2 border border-gray-100 w-72">
                              {bcgSubMenu === 'casey' && (
                                <>
                                  <li>
                                    <Link to="/bcg-casey-simulation" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                                      BCG Casey Simulation
                                    </Link>
                                  </li>
                                  <li>
                                    <Link to="/bcg-casey-guide" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                                      BCG Casey Guide
                                    </Link>
                                  </li>
                                </>
                              )}
                            </ul>
                          </div>
                        </>
                      )}
                    </div>
                  </li>
                    </div>
                  </motion.li>
                )}
                {/* Track: McKinsey — Lilli first, then SeaWolf, RedRock, Ecosystem */}
                {track === 'mck' && (
                  <motion.li
                    key="mck"
                    className="flex items-center space-x-6"
                    style={{ listStyle: 'none' }}
                    initial={{ opacity: 0, x: 14 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -14 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                  >
                    <div className="flex items-center space-x-6">
                  <li>
                    <Link to="/mckinsey-ai-coach" onClick={handleDropdownLinkClick} className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-inter text-[15px]">
                      McKinsey Lilli AI Assessment Coach
                    </Link>
                  </li>
                  {/* McKinsey SeaWolf Game dropdown */}
                  <li 
                    className="relative"
                    ref={seawolfDropdownRef}
                    onMouseEnter={() => handleMouseEnter('seawolf')}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      onClick={() => toggleDropdown('seawolf')}
                      className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-inter text-[15px] flex items-center gap-1"
                    >
                      McKinsey SeaWolf Game
                      <svg className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'seawolf' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <ul className={`absolute bg-white shadow-lg pt-2 rounded-lg ${activeDropdown === 'seawolf' ? 'block' : 'hidden'} text-left w-72 top-full transform translate-y-1`}>
                      <li>
                        <Link to="/seawolf-practice" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                          ⚡ McKinsey Sea Wolf Simulation
                        </Link>
                      </li>
                      <li>
                        <Link to="/seawolf" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                          🌊 McKinsey Sea Wolf Guide & Solver
                        </Link>
                      </li>
                    </ul>
                  </li>

                  {/* McKinsey RedRock dropdown */}
              <li 
                className="relative"
                ref={redrockDropdownRef}
                onMouseEnter={() => handleMouseEnter('redrock')}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  onClick={() => toggleDropdown('redrock')}
                  className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-inter text-[15px] flex items-center gap-1"
                >
                  McKinsey RedRock
                  <svg className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'redrock' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <ul className={`absolute bg-white shadow-lg pt-2 rounded-lg ${activeDropdown === 'redrock' ? 'block' : 'hidden'} text-left w-72 top-full transform translate-y-1`}>
                  <li>
                    <Link to="/redrock" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                      🔥 McKinsey Red Rock Simulator
                    </Link>
                  </li>
                  <li>
                    <Link to="/redrock-guide" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                      🏔️ McKinsey Red Rock Guide
                    </Link>
                  </li>
                </ul>
              </li>

                  {/* McKinsey Ecosystem Game dropdown */}
                  <li 
                    className="relative"
                    ref={ecosystemDropdownRef}
                    onMouseEnter={() => handleMouseEnter('ecosystem')}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      onClick={() => toggleDropdown('ecosystem')}
                      className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-inter text-[15px] flex items-center gap-1"
                    >
                      McKinsey Ecosystem Game
                      <svg className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'ecosystem' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <ul className={`absolute bg-white shadow-lg pt-2 rounded-lg ${activeDropdown === 'ecosystem' ? 'block' : 'hidden'} text-left w-72 top-full transform translate-y-1`}>
                      <li>
                        <Link to="/ecosystem-solver" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                          🚀 McKinsey Ecosystem Solver
                        </Link>
                      </li>
                      <li>
                        <Link to="/ecosystemguide" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                          🎯 McKinsey Ecosystem Guide
                        </Link>
                      </li>
                    </ul>
                  </li>
                    </div>
                  </motion.li>
                )}
                {/* Track: BCG — BCG Candidate-Led AI Coach first, then BCG Casey (Simulation, Guide only) */}
                {track === 'bcg' && (
                  <motion.li
                    key="bcg"
                    className="flex items-center space-x-6"
                    style={{ listStyle: 'none' }}
                    initial={{ opacity: 0, x: 14 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -14 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                  >
                    <div className="flex items-center space-x-6">
                  <li>
                    <Link to="/bcg-ai-coach" onClick={handleDropdownLinkClick} className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-inter text-[15px]">
                      BCG Candidate-Led AI Coach
                    </Link>
                  </li>
                  <li 
                    className="relative"
                    ref={bcgTrackDropdownRef}
                    onMouseEnter={() => handleMouseEnter('bcg_track')}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      onClick={() => toggleDropdown('bcg_track')}
                      className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-inter text-[15px] flex items-center gap-1"
                    >
                      BCG Casey
                      <svg className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'bcg_track' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <ul className={`absolute bg-white shadow-lg pt-2 rounded-lg ${activeDropdown === 'bcg_track' ? 'block' : 'hidden'} text-left w-72 top-full transform translate-y-1`}>
                      <li>
                        <Link to="/bcg-casey-simulation" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                          BCG Casey Simulation
                        </Link>
                      </li>
                      <li>
                        <Link to="/bcg-casey-guide" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                          BCG Casey Guide
                        </Link>
                      </li>
                    </ul>
                  </li>
                    </div>
                  </motion.li>
                )}
              </AnimatePresence>

              {/* Resources dropdown — shown for all tracks */}
              <li 
                className="relative"
                ref={resourcesDropdownRef}
                onMouseEnter={() => handleMouseEnter('resources')}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  onClick={() => toggleDropdown('resources')}
                  className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-inter text-[15px] flex items-center gap-1"
                >
                  Free Resources
                  <svg className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'resources' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <ul className={`absolute bg-white shadow-lg pt-2 rounded-lg ${activeDropdown === 'resources' ? 'block' : 'hidden'} text-left w-80 top-full transform translate-y-1`}>
                  <li>
                    <Link to="/about-solve" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                      📘 About McKinsey Solve
                    </Link>
                  </li>
                  <li>
                    <Link to="/about-bcg-casey" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                      📘 About BCG Casey
                    </Link>
                  </li>
                  <li>
                    <Link to="/consulting-math-guides" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                      📊 Quick Consulting Math & Chart Guides
                    </Link>
                  </li>
                  <li>
                    <Link to="/ecosystemguide" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                      🌱 Ecosystem Game Guide
                    </Link>
                  </li>
                  <li>
                    <Link to="/seawolf" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                      🌊 Sea Wolf Guide & Solver
                    </Link>
                  </li>
                  <li>
                    <Link to="/bcg-casey-guide" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                      📋 Casey Guide
                    </Link>
                  </li>
                  <li>
                    <Link to="/redrock-guide" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                      🏔️ Free Red Rock practice drills & guide
                    </Link>
                  </li>
                  <li>
                    <Link to="/ecosystem-solver" onClick={handleDropdownLinkClick} className="block px-4 py-2.5 hover:bg-blue-50 text-[14px] font-inter text-gray-600 hover:text-blue-600">
                      ⚡ Try our solver and see how fast it is
                    </Link>
                  </li>
                </ul>
              </li>
              <li>
                <Link to="/#pricing" className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-inter text-[15px]">
                  Pricing
                </Link>
              </li>
            </ul>
          </nav>
          {/* Login/Account button(s) - place here, outside nav/ul */}
          {user ? (
            <>
              <Link
                to="/account"
                className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-inter text-[15px] flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Account
              </Link>
              <button
                onClick={handleSignOut}
                className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-inter text-[15px] flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign Out
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate('/auth')}
              className="px-5 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg hover:shadow-md transition-all duration-200 font-inter text-[15px]"
            >
              Login
            </button>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`sm:hidden ${mobileMenuOpen ? 'block' : 'hidden'} bg-white border-t border-gray-100`}>
        <div className="px-4 py-2 space-y-1">
          {(isLanding || isBcgPage(pathname) || isMcKinseyPage(pathname)) && !isFreeResourcesPage(pathname) && (
            <div className="py-2 border-b border-gray-100">
              <TrackSwitcher />
            </div>
          )}
          {/* Track: All — McKinsey dropdown (Seawolf, Red Rock, Ecosystem + sub-links), BCG */}
          {track === 'all' && (
            <>
              <div>
                <button
                  type="button"
                  className="w-full flex justify-between items-center py-3 px-2 text-gray-700 hover:text-blue-600 font-inter text-base"
                  onClick={() => { if (mobileDropdown === 'mckinsey') setMobileMckinseySub(null); handleMobileDropdown('mckinsey'); }}
                >
                  McKinsey
                  <svg className={`w-5 h-5 ml-2 transition-transform duration-200 ${mobileDropdown === 'mckinsey' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {mobileDropdown === 'mckinsey' && (
                  <div className="pl-4 border-l border-blue-100 mb-2 space-y-1">
                    <Link to="/mckinsey-ai-coach" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm font-inter">
                      McKinsey Lilli AI Assessment Coach
                    </Link>
                    <div>
                      <button type="button" className="w-full flex justify-between items-center py-2 text-gray-600 hover:text-blue-600 text-sm font-inter" onClick={() => setMobileMckinseySub(mobileMckinseySub === 'seawolf' ? null : 'seawolf')}>
                        McKinsey SeaWolf Game
                        <svg className={`w-4 h-4 transition-transform duration-200 ${mobileMckinseySub === 'seawolf' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {mobileMckinseySub === 'seawolf' && (
                        <ul className="pl-3 border-l border-slate-200 mt-1">
                          <li><Link to="/seawolf-practice" onClick={handleDropdownLinkClick} className="block py-1.5 text-gray-500 hover:text-blue-600 text-sm">⚡ McKinsey Sea Wolf Simulation</Link></li>
                          <li><Link to="/seawolf" onClick={handleDropdownLinkClick} className="block py-1.5 text-gray-500 hover:text-blue-600 text-sm">🌊 McKinsey Sea Wolf Guide & Solver</Link></li>
                        </ul>
                      )}
                    </div>
                    <div>
                      <button type="button" className="w-full flex justify-between items-center py-2 text-gray-600 hover:text-blue-600 text-sm font-inter" onClick={() => setMobileMckinseySub(mobileMckinseySub === 'redrock' ? null : 'redrock')}>
                        McKinsey RedRock
                        <svg className={`w-4 h-4 transition-transform duration-200 ${mobileMckinseySub === 'redrock' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {mobileMckinseySub === 'redrock' && (
                        <ul className="pl-3 border-l border-slate-200 mt-1">
                          <li><Link to="/redrock" onClick={handleDropdownLinkClick} className="block py-1.5 text-gray-500 hover:text-blue-600 text-sm">🔥 McKinsey Red Rock Simulator</Link></li>
                          <li><Link to="/redrock-guide" onClick={handleDropdownLinkClick} className="block py-1.5 text-gray-500 hover:text-blue-600 text-sm">🏔️ McKinsey Red Rock Guide</Link></li>
                        </ul>
                      )}
                    </div>
                    <div>
                      <button type="button" className="w-full flex justify-between items-center py-2 text-gray-600 hover:text-blue-600 text-sm font-inter" onClick={() => setMobileMckinseySub(mobileMckinseySub === 'ecosystem' ? null : 'ecosystem')}>
                        McKinsey Ecosystem Game
                        <svg className={`w-4 h-4 transition-transform duration-200 ${mobileMckinseySub === 'ecosystem' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {mobileMckinseySub === 'ecosystem' && (
                        <ul className="pl-3 border-l border-slate-200 mt-1">
                          <li><Link to="/ecosystem-solver" onClick={handleDropdownLinkClick} className="block py-1.5 text-gray-500 hover:text-blue-600 text-sm">🚀 McKinsey Ecosystem Solver</Link></li>
                          <li><Link to="/ecosystemguide" onClick={handleDropdownLinkClick} className="block py-1.5 text-gray-500 hover:text-blue-600 text-sm">🎯 McKinsey Ecosystem Guide</Link></li>
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <button
                  type="button"
                  className="w-full flex justify-between items-center py-3 px-2 text-gray-700 hover:text-blue-600 font-inter text-base"
                  onClick={() => { if (mobileDropdown === 'bcg') setMobileBcgSub(null); handleMobileDropdown('bcg'); }}
                >
                  BCG
                  <svg className={`w-5 h-5 ml-2 transition-transform duration-200 ${mobileDropdown === 'bcg' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {mobileDropdown === 'bcg' && (
                  <div className="pl-4 border-l border-blue-100 mb-2 space-y-1">
                    <Link to="/bcg-ai-coach" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm font-inter">
                      BCG Candidate-Led AI Coach
                    </Link>
                    <div>
                      <button type="button" className="w-full flex justify-between items-center py-2 text-gray-600 hover:text-blue-600 text-sm font-inter" onClick={() => setMobileBcgSub(mobileBcgSub === 'casey' ? null : 'casey')}>
                        BCG Casey
                        <svg className={`w-4 h-4 transition-transform duration-200 ${mobileBcgSub === 'casey' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {mobileBcgSub === 'casey' && (
                        <ul className="pl-3 border-l border-slate-200 mt-1">
                          <li><Link to="/bcg-casey-simulation" onClick={handleDropdownLinkClick} className="block py-1.5 text-gray-500 hover:text-blue-600 text-sm">BCG Casey Simulation</Link></li>
                          <li><Link to="/bcg-casey-guide" onClick={handleDropdownLinkClick} className="block py-1.5 text-gray-500 hover:text-blue-600 text-sm">BCG Casey Guide</Link></li>
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          {/* Track: McKinsey — Lilli first, then SeaWolf, RedRock, Ecosystem mobile */}
          {track === 'mck' && (
            <>
          <Link to="/mckinsey-ai-coach" onClick={handleDropdownLinkClick} className="block py-3 px-2 text-gray-700 hover:text-blue-600 font-inter text-base">
            McKinsey Lilli AI Assessment Coach
          </Link>
          <div>
            <button
              className="w-full flex justify-between items-center py-3 px-2 text-gray-700 hover:text-blue-600 font-inter text-base"
              onClick={() => handleMobileDropdown('seawolf')}
            >
              McKinsey SeaWolf Game
              <svg className={`w-5 h-5 ml-2 transition-transform duration-200 ${mobileDropdown === 'seawolf' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {mobileDropdown === 'seawolf' && (
              <ul className="pl-4 border-l border-blue-100 mb-2">
                <li>
                  <Link to="/seawolf-practice" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                    ⚡ McKinsey Sea Wolf Simulation
                  </Link>
                </li>
                <li>
                  <Link to="/seawolf" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                    🌊 McKinsey Sea Wolf Guide & Solver
                  </Link>
                </li>
              </ul>
            )}
          </div>

          <div>
            <button
              className="w-full flex justify-between items-center py-3 px-2 text-gray-700 hover:text-blue-600 font-inter text-base"
              onClick={() => handleMobileDropdown('redrock')}
            >
              McKinsey RedRock
              <svg className={`w-5 h-5 ml-2 transition-transform duration-200 ${mobileDropdown === 'redrock' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {mobileDropdown === 'redrock' && (
              <ul className="pl-4 border-l border-blue-100 mb-2">
                <li>
                  <Link to="/redrock" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                    🔥 McKinsey Red Rock Simulator
                  </Link>
                </li>
                <li>
                  <Link to="/redrock-guide" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                    🏔️ McKinsey Red Rock Guide
                  </Link>
                </li>
              </ul>
            )}
          </div>

          <div>
            <button
              className="w-full flex justify-between items-center py-3 px-2 text-gray-700 hover:text-blue-600 font-inter text-base"
              onClick={() => handleMobileDropdown('ecosystem')}
            >
              McKinsey Ecosystem Game
              <svg className={`w-5 h-5 ml-2 transition-transform duration-200 ${mobileDropdown === 'ecosystem' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {mobileDropdown === 'ecosystem' && (
              <ul className="pl-4 border-l border-blue-100 mb-2">
                <li>
                  <Link to="/ecosystem-solver" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                    🚀 McKinsey Ecosystem Solver
                  </Link>
                </li>
                <li>
                  <Link to="/ecosystemguide" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                    🎯 McKinsey Ecosystem Guide
                  </Link>
                </li>
              </ul>
            )}
          </div>
            </>
          )}
          {/* Track: BCG — BCG Candidate-Led AI Coach first, then BCG Casey (Simulation, Guide only) */}
          {track === 'bcg' && (
            <>
              <Link to="/bcg-ai-coach" onClick={handleDropdownLinkClick} className="block py-3 px-2 text-gray-700 hover:text-blue-600 font-inter text-base">
                BCG Candidate-Led AI Coach
              </Link>
              <div>
                <button
                  className="w-full flex justify-between items-center py-3 px-2 text-gray-700 hover:text-blue-600 font-inter text-base"
                  onClick={() => handleMobileDropdown('bcg_track')}
                >
                  BCG Casey
                  <svg className={`w-5 h-5 ml-2 transition-transform duration-200 ${mobileDropdown === 'bcg_track' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {mobileDropdown === 'bcg_track' && (
                  <ul className="pl-4 border-l border-blue-100 mb-2">
                    <li>
                      <Link to="/bcg-casey-simulation" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                        BCG Casey Simulation
                      </Link>
                    </li>
                    <li>
                      <Link to="/bcg-casey-guide" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                        BCG Casey Guide
                      </Link>
                    </li>
                  </ul>
                )}
              </div>
            </>
          )}

          {/* Free Resources — shown for all tracks */}
          {(track === 'all' || track === 'mck' || track === 'bcg') && (
          <div>
            <button
              className="w-full flex justify-between items-center py-3 px-2 text-gray-700 hover:text-blue-600 font-inter text-base"
              onClick={() => handleMobileDropdown('resources')}
            >
              Free Resources
              <svg className={`w-5 h-5 ml-2 transition-transform duration-200 ${mobileDropdown === 'resources' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {mobileDropdown === 'resources' && (
              <ul className="pl-4 border-l border-blue-100 mb-2">
                <li>
                  <Link to="/about-solve" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                    📘 About McKinsey Solve
                  </Link>
                </li>
                <li>
                  <Link to="/about-bcg-casey" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                    📘 About BCG Casey
                  </Link>
                </li>
                <li>
                  <Link to="/consulting-math-guides" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                    📊 Quick Consulting Math & Chart Guides
                  </Link>
                </li>
                <li>
                  <Link to="/ecosystemguide" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                    🌱 Ecosystem Game Guide
                  </Link>
                </li>
                <li>
                  <Link to="/seawolf" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                    🌊 Sea Wolf Guide & Solver
                  </Link>
                </li>
                <li>
                  <Link to="/bcg-casey-guide" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                    📋 Casey Guide
                  </Link>
                </li>
                <li>
                  <Link to="/redrock-guide" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                    🏔️ Free Red Rock practice drills & guide
                  </Link>
                </li>
                <li>
                  <Link to="/ecosystem-solver" onClick={handleDropdownLinkClick} className="block py-2 text-gray-600 hover:text-blue-600 text-sm">
                    ⚡ Try our solver and see how fast it is
                  </Link>
                </li>
              </ul>
            )}
          </div>
          )}
          <Link to="/#pricing" onClick={handlePricingClick} className="block py-3 px-2 text-gray-700 hover:text-blue-600 font-inter text-base">
            Pricing
          </Link>
          {user ? (
            <>
              <Link to="/account" onClick={handleDropdownLinkClick} className="block py-3 px-2 text-gray-700 hover:text-blue-600 font-inter text-base">
                Account
              </Link>
              <button
                onClick={handleSignOut}
                className="w-full mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-inter text-base flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign Out
              </button>
            </>
          ) : (
            <button
              onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}
              className="w-full mt-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg hover:shadow-md transition-all duration-200 font-inter text-base"
            >
              Login
            </button>
          )}
        </div>
      </div>
    </nav>
  );
}

export default Header;
